import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CommandCentreResponse } from "@/api/intelligence";
import { formatCount } from "@/lib/intelligenceFormat";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";

type Props = {
  data: CommandCentreResponse;
};

export function PatientFunnelSection({ data }: Props) {
  const maxValue = Math.max(
    ...data.funnel.stages.filter((s) => s.available && s.value !== null).map((s) => s.value ?? 0),
    1
  );

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Patient &amp; Funnel</h2>
        <p className="text-sm text-muted-foreground">
          Validated Intelligence KPIs · conversion rates shown only when reliable
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Funnel stages</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.funnel.stages.map((stage) => (
            <div key={stage.key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">{stage.label}</span>
                {stage.available ? (
                  <span className="tabular-nums font-semibold">{formatCount(stage.value ?? 0)}</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Unavailable
                  </span>
                )}
              </div>
              {stage.available ? (
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500/80 transition-all"
                    style={{ width: `${Math.max(4, ((stage.value ?? 0) / maxValue) * 100)}%` }}
                  />
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">{stage.unavailableReason}</p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {data.funnel.conversions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">Conversion rates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.funnel.conversions.map((c) => (
              <div
                key={`${c.fromKey}-${c.toKey}`}
                className={cn(
                  "flex items-start justify-between gap-4 rounded-lg border px-3 py-2 text-sm",
                  !c.available && "bg-muted/30"
                )}
              >
                <span className="text-slate-700">{c.label}</span>
                {c.available && c.percent !== null ? (
                  <span className="font-semibold tabular-nums">{c.percent.toFixed(1)}%</span>
                ) : (
                  <span className="text-xs text-muted-foreground text-right max-w-[220px]">
                    {c.unavailableReason ?? "Not available"}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
